import React, { useEffect, useState } from 'react';
import Modal from './ModalTrue';
import {
  getFinalResponseOptions, previewFinalResponse, confirmFinalResponse, retryFinalResponse,
  FinalResponseOptions, FinalResponsePreview, SubmissionState,
  SubmissionError,
} from '../api/finalResponses';
import SubmissionErrorDetails from './SubmissionErrorDetails';

const messages: Record<SubmissionState, string> = {
  accepted: 'The claim is permanently closed. The email was accepted by the email provider; delivery is not yet confirmed.',
  failed: 'The claim remains permanently closed. The email was not sent: submission failed. You can retry the saved response.',
  unknown: 'The claim is permanently closed. Submission is uncertain: check the provider status before taking any further action.',
  pending: 'The claim is permanently closed. Submission has not completed: check the provider status before taking any further action.',
  sending: 'The claim is permanently closed. Submission is in progress or awaiting confirmation: check the provider status before taking any further action.',
};

export default function FinalResponseModal({ claimId, onClose, onChanged }: {
  claimId: string; onClose: () => void; onChanged: () => void;
}) {
  const [options, setOptions] = useState<FinalResponseOptions | null>(null);
  const [sourceTaskId, setSource] = useState('');
  const [responseDate, setDate] = useState('');
  const [reasonCodes, setReasons] = useState<string[]>([]);
  const [preview, setPreview] = useState<FinalResponsePreview | null>(null);
  const [state, setState] = useState<SubmissionState | null>(null);
  const [submissionError, setSubmissionError] = useState<SubmissionError | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const selection = { sourceTaskId, responseDate, reasonCodes };

  useEffect(() => {
    let active = true;
    getFinalResponseOptions(claimId).then(value => {
      if (active) {
        setOptions(value); setState(value.closure?.state || null);
        setSubmissionError(value.closure?.state === 'accepted' ? null : value.closure?.submissionError || null);
      }
    }).catch(err => { if (active) setError(err.response?.data?.error || 'Could not load final-response options.'); });
    return () => { active = false; };
  }, [claimId]);

  const run = async (action: 'preview' | 'confirm' | 'retry') => {
    if (busy || (action === 'retry' && state !== 'failed')) return;
    setBusy(true); setError('');
    try {
      if (action === 'preview') {
        setPreview(await previewFinalResponse(claimId, selection));
      } else {
        const result = action === 'retry' ? await retryFinalResponse(claimId) : await confirmFinalResponse(
          claimId, { ...selection, previewToken: preview!.previewToken, confirmed: true },
        );
        setState(result.state); setSubmissionError(result.state === 'accepted' ? null : result.submissionError || null);
        setPreview(null); onChanged();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'The action could not finish. Refresh its status before trying again.');
      if (action !== 'preview') {
        // A browser timeout is not evidence of rejection; reload durable state.
        setPreview(null); setState('unknown'); setSubmissionError(null); onChanged();
        try {
          const fresh = await getFinalResponseOptions(claimId);
          setState(fresh.closure?.state || 'unknown');
          setSubmissionError(fresh.closure?.state === 'accepted' ? null : fresh.closure?.submissionError || null);
        } catch (_) { /* retain uncertainty */ }
      }
    } finally { setBusy(false); }
  };

  return <Modal onClose={() => { if (!busy) onClose(); }}>
    <section role="dialog" aria-modal="true" aria-labelledby="final-response-title" style={{ maxWidth: 820 }}>
      <h2 id="final-response-title">Final response</h2>
      <p>This action permanently closes this claim and sends the selected lender response to the client. It does not trigger billing.</p>
      {error && <p role="alert">{error}</p>}
      {!options && !error && <p>Loading…</p>}
      {state ? <>
        <p role="status">{messages[state]}</p>
        <SubmissionErrorDetails error={submissionError} />
        {state === 'failed' && <button disabled={busy} onClick={() => run('retry')}>Retry saved response</button>}
      </> : options && <>
        <fieldset disabled={busy}>
          <legend>Lender determination</legend>
          <label htmlFor="fr-source">Lender response email</label>
          <select id="fr-source" value={sourceTaskId} onChange={event => { setSource(event.target.value); setPreview(null); }}>
            <option value="">Select a lender email</option>
            {options.sources.map(source => <option key={source.id} value={source.id}>{source.subject} — {source.received_date}</option>)}
          </select>
          <p>Only non-archived lender emails are shown; completed emails remain available.</p>
          {options.sources.length === 0 && <p>No non-archived lender email is assigned to this claim. Assign the correct source email before continuing.</p>}
          <label htmlFor="fr-date">Lender response date</label>
          <input id="fr-date" type="date" value={responseDate} onChange={event => { setDate(event.target.value); setPreview(null); }} />
          <p>Select only the reasons confirmed by the lender.</p>
          {options.reasons.map(reason => <label key={reason.code} style={{ display: 'block', margin: '10px 0' }}>
            <input type="checkbox" checked={reasonCodes.includes(reason.code)} onChange={event => {
              setReasons(values => event.target.checked ? [...values, reason.code] : values.filter(value => value !== reason.code)); setPreview(null);
            }} /> {reason.label}
          </label>)}
        </fieldset>
        <button disabled={busy || !sourceTaskId || !responseDate || reasonCodes.length === 0} onClick={() => run('preview')}>Preview letter</button>
        {preview && <>
          <h3>Client letter preview</h3>
          <p>To: {preview.recipient}</p><p>Attachment: {preview.attachmentName}</p>
          <div style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflowY: 'auto', border: '1px solid #aaa', padding: 16 }}>{preview.text}</div>
          <p>Confirm that the date, selected reasons and source email are correct. Nobody, including an administrator, will be able to reinstate this claim.</p>
          <button disabled={busy} onClick={() => run('confirm')}>Send final response and permanently close claim</button>
        </>}
      </>}
      <button disabled={busy} onClick={onClose}>Close</button>
    </section>
  </Modal>;
}
